import { CaseFlowFacts, UserUploadedDocument } from '@rabbit/data/types';
import { FullMetadata } from 'firebase/storage';

/**
 * Default accepted file types for file uploaders.
 * Includes images, PDFs, and common video formats.
 */
export const DEFAULT_ACCEPTED_FILE_TYPES = [
  'image/*',
  '.pdf',
  'video/mp4',
  'video/quicktime', // .mov
  'video/x-msvideo', // .avi
  'video/webm',
  '.mp4',
  '.mov',
  '.avi',
  '.webm',
  '.hevc',
  '.m4v',
];

/**
 * Human-readable description of accepted file types for UI display.
 */
export const ACCEPTED_FILE_TYPES_DESCRIPTION = 'PDF, PNG, JPG, MP4, MOV or HEVC';

export type UploadedFileCategoryShape =
  | 'consumer_claim_evidence'
  | 'consumer_proof_purchase'
  | 'chat_attachment'
  | 'note_attachment'
  | 'vendable_manuals'
  | 'vendable_images'
  | 'srv_images'
  | 'receipts'
  | 'postage_receipts'
  | 'user_profile_img'
  | 'internal_case_files'
  | 'tenant_logo'
  | 'repairer_profile_image';

export interface UploadedFilesStateShape {
  filesArr: UserUploadedDocument[];
  cat: UploadedFileCategoryShape;
}

// File uploads and previews
export interface DraggedFilePreviewShape {
  type: string;
  url?: string;
}
export interface DraggedFileShape extends Partial<File> {
  id: string;
  extension?: string;
  preview: DraggedFilePreviewShape;
  sizeReadable?: string;
  lastModified?: number;
  lastModifiedDate?: Date;
  name: string;
  size: number;
  type: string;
  webkitRelativePath?: string;
}

export interface UserUploadedFileShape {
  url: string;
  metadata?: FullMetadata;
  name: string;
}

export type IncomingOrOutgoingUserFileShape = Partial<
  DraggedFileShape & UserUploadedFileShape
>;

export enum DocTypeShapeTypes {
  Holding = 'holding',
  Vendable = 'vendable',
  Case = 'case',
  Note = 'note',
}
export interface DocTypeShape {
  type: DocTypeShapeTypes;
  docid: string | null;
}

export interface FileUploadHandlersShape {
  category: UploadedFileCategoryShape;
  uploadFiles: (
    filesToUpload: File[],
    personaId: string,
    fileCategory: UploadedFileCategoryShape,
    docType?: DocTypeShape
  ) => Promise<void>;
  uploadedFiles?: UploadedFilesStateShape | null;
  uploadedTempFiles?: UploadedFilesStateShape | null;
  deleteFile?: (urlOrPath: string) => Promise<void>;
  clearFileFromState?: (filePath: string) => Promise<void>;
  isUpdating?: boolean;
  uploadProgress?: number | null;
  uploadTaskCat?: UploadedFileCategoryShape | null;
  updateHoldingWithFiles?: (
    holdingId: string,
    filesArr?: UserUploadedDocument[]
  ) => Promise<void>;
  clearFileFromHolding?: (holdingId: string, filePath: string) => Promise<void>;
  deleteUnusedTempFiles?: (filesArr?: UserUploadedDocument[]) => Promise<void>;
}

export enum UploadedFileCategories {
  ConsumerCaseEvidence = 'consumer_claim_evidence', // todo change to case here
  ConsumerProofPurchase = 'consumer_proof_purchase',
  SerialNumberProof = 'serial_number_proof',
  ChatAttachment = 'chat_attachment',
  VendableManuals = 'vendable_manuals',
  VendableImages = 'vendable_images',
  SerialNumberHelpImage = 'serial_number_help_image',
  SRVImages = 'srv_images',
  PostageReceipts = 'postage_receipts',
  Receipts = 'receipts',
  UserProfileImage = 'user_profile_img',
  InternalCaseFiles = 'internal_case_files',
  ShippingLabel = 'shipping_label',
  ExternalRepairInvoices = 'external_repair_invoices',
  AssessmentAttachment = 'assessment_attachment',
  installation_attachment = 'installation_attachment',
  NotesImages = 'notes_images',
}

export type PersonaTypeShape =
  | 'Consumer'
  | 'Manufacturer'
  | 'Repairer'
  | 'Installer'
  | null;

export interface OngoingUploadShape {
  key: string;
  progress: number;
  docType?: DocTypeShape;
  category: UploadedFileCategories;
  file: DraggedFileShape;
}

export interface CompletedUploadShape {
  key: string;
  ogFile: DraggedFileShape;
  docType?: DocTypeShape;
  category: UploadedFileCategories;
  error: string | null;
  uploadedFile: UserUploadedDocument | null;
  /** When the upload finished, so cleanup can tell an abandoned file from one
   *  whose "mark as attached" step is still in flight. */
  completedAt?: number;
}

export interface UploadQueueStateShape {
  ongoing: OngoingUploadShape[];
  completed: CompletedUploadShape[];
  attached: CompletedUploadShape[];
}

/* ------------------------------ Wrapper types ----------------------------- */
export enum FileUploaderVariations {
  FormItem = 'form-item',
  InputPicture = 'input-picture',
}
export interface UploadIdentifiersShape {
  personaId: string;
  docType: DocTypeShape | null; // todo: review if we need both docType and category also destructure this
  category: UploadedFileCategories;
}
export interface UploadActionsShape {
  uploadFiles: (
    filesToUpload: File[],
    personaId: string,
    fileCategory: UploadedFileCategories,
    docType?: DocTypeShape
  ) => Promise<void>;
  deleteFile: (urlOrPath: string, props: DeleteFilePropsShape) => Promise<void>;
  alterCaseFacts?: (facts: CaseFlowFacts) => Promise<void>;
  setShouldRefetch?: (value: boolean) => void;
}

export interface FileUploaderWrapperProps {
  label?: string;
  labelClasses?: string;
  identifiers: UploadIdentifiersShape;
  variation?: FileUploaderVariations;
  currentFiles?: any[];
  accepts: string[];
  onUploadCompleted?: (...args: any) => void;
  onDeleteFile?: (...args: any) => void;
  allowDelete?: boolean;
  alterCaseFacts?: (facts: { [key: string]: any }) => Promise<void>;
  shouldAutoUpdateDocs?: boolean;
  name?: string;
  refs?: {
    selectFileRef: React.MutableRefObject<HTMLElement | undefined> | null;
    removeFileRef: React.MutableRefObject<HTMLElement | undefined> | null;
  };
  maxFiles?: number;
  disabled?: boolean;
}

/* ------------------------------- Misc types ------------------------------- */
export interface DeleteFilePropsShape {
  category: UploadedFileCategories;
  docType?: DocTypeShape | null;
  currentFiles?: UserUploadedDocument[];
  alterCaseFacts?: (facts: { [key: string]: any }) => Promise<void>;
  unattached?: boolean;
  vendableFound?: boolean;
}


/**
 * Converts an accepts array into a human-readable string.
 * e.g., ['image/*', '.pdf', 'video/mp4', '.mov'] → "Images, PDF, MP4, MOV"
 */
export const formatAcceptedFileTypesToReadable = (accepts: string[]): string => {
  const readableTypes = new Set<string>();

  for (const type of accepts) {
    // Handle wildcard types like 'image/*', 'video/*'
    if (type === 'image/*') {
      readableTypes.add('Images');
    } else if (type === 'video/*') {
      readableTypes.add('Videos');
    } else if (type.startsWith('.')) {
      // Handle extensions like '.pdf', '.mp4'
      readableTypes.add(type.slice(1).toUpperCase());
    } else if (type.includes('/')) {
      // Handle MIME types like 'video/mp4', 'video/quicktime'
      const subtype = type.split('/')[1];
      if (subtype === 'quicktime') {
        readableTypes.add('MOV');
      } else if (subtype === 'x-msvideo') {
        readableTypes.add('AVI');
      } else if (subtype) {
        readableTypes.add(subtype.toUpperCase());
      }
    }
  }

  return Array.from(readableTypes).join(', ');
};
